/**
 * SmartArt helpers — pure, DOM-free utilities shared by the editor and PPTX export.
 *
 * A SmartArt object stores a flat, ordered list of nodes, each with an integer `level`
 * (0 = top). This is the natural model for the text pane (indent = level) and converts
 * cleanly to a tree for hierarchy layouts.
 */

import { v4 } from 'uuid';

/** Reference design size the layout engine positions against (percentages, not pixels). */
export const SMART_ART_REF_WIDTH = 100;
export const SMART_ART_REF_HEIGHT = 100;

export const SMART_ART_LAYOUTS = ['list', 'process', 'cycle', 'hierarchy', 'pyramid'];

const INDENT = '  '; // two spaces per outline level

export const makeNodeId = () => `node-${v4()}`;

/** Normalize/clamp node levels so the first node is level 0 and levels never jump by >1. */
export const normalizeNodes = (nodes = []) => {
  const cleaned = [];
  let prevLevel = 0;
  nodes.forEach((node, index) => {
    let level = Number.isFinite(node.level) ? Math.max(0, Math.floor(node.level)) : 0;
    if (index === 0) level = 0;
    else if (level > prevLevel + 1) level = prevLevel + 1;
    prevLevel = level;
    cleaned.push({
      id: node.id || makeNodeId(),
      text: node.text ?? '',
      level,
      ...(node.delta ? { delta: node.delta } : {}),
    });
  });
  return cleaned;
};

/** Flat nodes -> indented outline text (one node per line). */
export const nodesToText = (nodes = []) =>
  nodes.map((n) => INDENT.repeat(n.level) + (n.text ?? '')).join('\n');

/** Indented outline text -> flat nodes (preserves ids where a matching old node exists). */
export const textToNodes = (text = '', existingNodes = []) => {
  const lines = text.split('\n');
  const result = [];
  lines.forEach((rawLine, index) => {
    if (rawLine.trim() === '' && index === lines.length - 1 && lines.length > 1) return;
    const match = rawLine.match(/^(\s*)/);
    const leading = match ? match[1].replace(/\t/g, INDENT) : '';
    const level = Math.floor(leading.length / INDENT.length);
    const existing = existingNodes[result.length];
    const text = rawLine.trim();
    result.push({
      id: existing?.id || makeNodeId(),
      text,
      level,
      // keep rich-text formatting only while the plain text is untouched;
      // retyping a line in the text pane resets it (PowerPoint behavior)
      ...(existing?.delta && existing.text === text ? { delta: existing.delta } : {}),
    });
  });
  return normalizeNodes(result.length ? result : [{ id: makeNodeId(), text: '', level: 0 }]);
};

/** Build a tree ({...node, children:[]}) from flat, level-encoded nodes. */
export const buildTree = (nodes = []) => {
  const roots = [];
  const stack = []; // stack[level] = last node seen at that level
  nodes.forEach((node) => {
    const item = { ...node, children: [] };
    if (node.level === 0 || stack.length === 0) {
      roots.push(item);
      stack[0] = item;
      stack.length = 1;
    } else {
      const parentLevel = Math.min(node.level - 1, stack.length - 1);
      const parent = stack[parentLevel] || stack[stack.length - 1];
      (parent.children || (parent.children = [])).push(item);
      stack[node.level] = item;
      stack.length = node.level + 1;
    }
  });
  return roots;
};

/** Default color palettes keyed by theme name. */
export const SMART_ART_PALETTES = {
  blue: ['#4285F4', '#5B9BFF', '#1A73E8', '#7BAAF7', '#3367D6'],
  green: ['#34A853', '#5BB974', '#1E8E3E', '#81C995', '#188038'],
  warm: ['#FBBC04', '#F9AB00', '#EA8600', '#FDD663', '#E37400'],
  purple: ['#A142F4', '#B86CF6', '#8430CE', '#C58AF9', '#6E1FB8'],
};

export const DEFAULT_THEME = {
  name: 'blue',
  palette: SMART_ART_PALETTES.blue,
  textColor: '#ffffff',
  connectorColor: '#9aa0a6',
};

/** Pick a fill color for a node index, cycling through the palette. */
export const paletteColor = (theme, index) => {
  const palette = theme?.palette?.length ? theme.palette : DEFAULT_THEME.palette;
  return palette[index % palette.length];
};

/**
 * Quill delta -> flat SmartArt nodes. Each line becomes a node; the line's
 * `indent` block attribute (on its terminating newline op) becomes the node
 * `level`. Empty lines are dropped and inline formatting is ignored. Returns []
 * when there is no usable text.
 */
export const deltaToNodes = (delta) => {
  const ops = delta && Array.isArray(delta.ops) ? delta.ops : null;
  if (!ops) return [];

  const lines = [];
  let currentText = '';

  const pushLine = (indent) => {
    lines.push({ text: currentText, indent: Number.isFinite(indent) ? indent : 0 });
    currentText = '';
  };

  ops.forEach((op) => {
    if (typeof op.insert !== 'string') return; // skip embedded objects (images)
    const indent = op.attributes && op.attributes.indent;
    const segments = op.insert.split('\n');
    segments.forEach((seg, i) => {
      currentText += seg;
      if (i < segments.length - 1) pushLine(indent); // a newline terminates this line
    });
  });
  if (currentText.length > 0) pushLine(0); // trailing text with no final newline

  const nodes = lines
    .map((l) => ({ id: makeNodeId(), text: l.text.trim(), level: l.indent }))
    .filter((n) => n.text !== '');

  return nodes.length ? normalizeNodes(nodes) : [];
};

const escapeHtml = (s) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Only hex/rgb(a) colors may reach the generated style attribute. */
const isSafeCssColor = (c) => /^(#[0-9a-f]{3,8}|rgba?\(\s*[\d.,\s%]+\s*\))$/i.test(String(c || ''));

/**
 * Quill delta -> inline HTML for read-only node display. Whitelist-only:
 * bold/italic/underline/strike, color, background, size (px), font. Text is
 * escaped and every tag is built here, so the output is safe for
 * dangerouslySetInnerHTML. Returns '' for anything malformed.
 */
export const ALIGN_VALUES = new Set(['left', 'center', 'right', 'justify']);

// Inline HTML for one text run (bold/italic/color/background/size/font/decoration).
// XSS-safe: text is escaped and every style value is whitelist-validated.
const inlineHtmlForOp = (text, a) => {
  let part = escapeHtml(text).replace(/\n/g, '<br>');
  const styles = [];
  if (isSafeCssColor(a.color)) styles.push(`color:${a.color}`);
  if (isSafeCssColor(a.background)) styles.push(`background-color:${a.background}`);
  if (/^\d+(\.\d+)?px$/.test(String(a.size || ''))) styles.push(`font-size:${a.size}`);
  if (/^[\w\s-]+$/.test(String(a.font || ''))) styles.push(`font-family:'${a.font}'`);
  if (a.underline || a.strike) {
    const deco = [a.underline && 'underline', a.strike && 'line-through'].filter(Boolean).join(' ');
    styles.push(`text-decoration:${deco}`);
  }
  if (a.bold) part = `<strong>${part}</strong>`;
  if (a.italic) part = `<em>${part}</em>`;
  if (styles.length) part = `<span style="${styles.join(';')}">${part}</span>`;
  return part;
};

/**
 * Serialize a Quill delta to inline HTML for a SmartArt node.
 * - No line carries a whitelisted `align` -> inline parts joined by <br> (legacy output).
 * - Some line carries a whitelisted `align` -> each line becomes a <div>, aligned
 *   lines get `style="text-align:X"`, empty lines render as <div><br></div>.
 * Quill stores line alignment on the '\n' op that terminates the line.
 */
export const deltaToInlineHtml = (delta) => {
  const ops = delta && Array.isArray(delta.ops) ? delta.ops : null;
  if (!ops) return '';

  const lines = [];
  let cur = { html: '', align: null };
  let anyAlign = false;
  const endLine = (align) => {
    if (ALIGN_VALUES.has(align)) { cur.align = align; anyAlign = true; }
    lines.push(cur);
    cur = { html: '', align: null };
  };

  ops.forEach((op) => {
    if (typeof op.insert !== 'string') return;
    const a = op.attributes || {};
    const segments = op.insert.split('\n');
    segments.forEach((seg, i) => {
      if (i > 0) endLine(a.align); // the '\n' preceding this segment terminates a line
      if (seg) cur.html += inlineHtmlForOp(seg, a);
    });
  });
  if (cur.html) lines.push(cur); // content after a missing terminal newline (rare)

  // Quill deltas always end with '\n'; drop trailing empty, unaligned lines.
  while (lines.length && !lines[lines.length - 1].html && !lines[lines.length - 1].align) {
    lines.pop();
  }

  if (!anyAlign) {
    return lines.map((l) => l.html).join('<br>');
  }
  return lines
    .map((l) => `<div${l.align ? ` style="text-align:${l.align}"` : ''}>${l.html || '<br>'}</div>`)
    .join('');
};

/** First whitelisted line alignment in a node's delta, else 'center'. */
export const nodeAlign = (delta) => {
  const ops = delta && Array.isArray(delta.ops) ? delta.ops : null;
  if (!ops) return 'center';
  for (const op of ops) {
    const a = op && op.attributes && op.attributes.align;
    if (ALIGN_VALUES.has(a)) return a;
  }
  return 'center';
};

/** Lighten a #rrggbb color toward white by `amount` (0..1). Returns input unchanged if unparseable. */
export const tintColor = (hex, amount = 0.5) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return hex;
  const num = parseInt(m[1], 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  const t = (c) => Math.round(c + (255 - c) * amount);
  return `#${((1 << 24) + (t(r) << 16) + (t(g) << 8) + t(b)).toString(16).slice(1)}`;
};

/** Darken a #rrggbb color toward black by `amount` (0..1). Returns input unchanged if unparseable. */
export const shadeColor = (hex, amount = 0.5) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return hex;
  const num = parseInt(m[1], 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  const s = (c) => Math.round(c * (1 - amount));
  return `#${((1 << 24) + (s(r) << 16) + (s(g) << 8) + s(b)).toString(16).slice(1)}`;
};

/** Theme name reserved for the user-picked base color in the properties panel. */
export const CUSTOM_THEME_NAME = 'custom';

/** rgb()/rgba()/#rgb/#rrggbb -> #rrggbb, or null when unparseable. Alpha is dropped. */
const toHexColor = (color) => {
  const str = String(color || '').trim();
  if (/^#?[0-9a-f]{6}$/i.test(str)) return str.startsWith('#') ? str : `#${str}`;
  const short = /^#?([0-9a-f]{3})$/i.exec(str);
  if (short) return `#${short[1].split('').map((c) => c + c).join('')}`;
  const rgb = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i.exec(str);
  if (rgb) {
    const to2 = (n) => Math.min(255, parseInt(n, 10)).toString(16).padStart(2, '0');
    return `#${to2(rgb[1])}${to2(rgb[2])}${to2(rgb[3])}`;
  }
  return null;
};

/**
 * Map the properties-panel background/border state onto inline CSS for the
 * SmartArt container div. Always emits explicit values ('transparent'/'none')
 * so a new apply overwrites whatever a previous apply stored in styles.
 */
export const smartArtContainerStyles = ({ isTransparent, bgColor, borderOn, borderColor, borderWidth, borderRadius }) => {
  const width = Number(borderWidth);
  const radius = Number(borderRadius);
  return {
    backgroundColor: isTransparent || !bgColor ? 'transparent' : bgColor,
    borderStyle: borderOn ? 'solid' : 'none',
    borderColor: borderColor || 'rgba(0,0,0,1)',
    borderWidth: `${Number.isFinite(width) && width > 0 ? width : 1}px`,
    // Radius belongs to the border in the panel UI, so it resets with it.
    borderRadius: `${borderOn && Number.isFinite(radius) && radius > 0 ? radius : 0}px`,
  };
};

/**
 * One base color -> 5-shade monochromatic palette, following the same rhythm
 * as the built-in palettes (base, lighter, darker, lighter-still, darkest).
 * Falls back to the blue palette when the input can't be parsed.
 */
export const makeCustomPalette = (baseColor) => {
  const base = toHexColor(baseColor);
  if (!base) return SMART_ART_PALETTES.blue;
  return [
    base,
    tintColor(base, 0.25),
    shadeColor(base, 0.15),
    tintColor(base, 0.45),
    shadeColor(base, 0.3),
  ];
};
